import {
  OrderFeature,
  PROPERTY_GRID_SUBJECT_PROPERTY,
  PropertyDataWithOptionalReadonly,
  expected,
} from '@aloft/toolkit-shared';
import { Block, Flex, Grid, Icon, theme } from '@aloft/toolkit-sui';

import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';

import { useOrchestrateAutofillPropertyAPI } from '../../hooks/api/toolkitComponents.ts';
import { logger } from '../../logging/logging.ts';
import { selectOrderFeatures } from '../../redux/features/selectors/orderSelectors.ts';
import { selectPropertyDataById } from '../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { selectFeatureGridExpandedPropertyId } from '../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../redux/types.ts';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary.tsx';
import { LoadingScreen } from '../LoadingScreen/LoadingScreen.tsx';
import { HeaderPropertyGridSection } from './columnSections/HeaderPropertyGridSection.tsx';
import {
  PropertyGridColumnDefinition,
  PropertyGridColumnSectionComponent,
  PropertyGridForm,
  PropertyGridRowFieldType,
  PropertyGridSectionProps,
  PropertyGridType,
} from './types.ts';

const log = logger();

type ColumnErrorBoundaryFallbackProps = {
  error: Error;
  errorString: string;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ColumnErrorBoundaryFallback = ({ error, errorString }: ColumnErrorBoundaryFallbackProps) => (
  <Flex
    flexDirection="column"
    width="100%"
    maxWidth="350px"
    height="100%"
    alignItems="center"
    justifyContent="center"
    gap="8px"
    textAlign="center"
  >
    <Icon icon="error-warning" color="alertErrorLight" size="xxl" />
    <Block __dangerDoNotUseSx={{ fontWeight: 'bold' }}>Error loading column</Block>
    <Block width="100%">{error.message}</Block>
  </Flex>
);

type PropertyGridColumnProps = Pick<PropertyGridColumnDefinition, 'propertyId' | 'type'> & {
  columnSections: PropertyGridColumnSectionComponent[];
  fieldTypes: PropertyGridRowFieldType[];
  gridType: PropertyGridType;
  isDataSourceDrawerOpen: boolean;
};

export const PropertyGridColumn = ({
  columnSections,
  fieldTypes,
  gridType,
  isDataSourceDrawerOpen,
  propertyId,
  type,
}: PropertyGridColumnProps) => {
  const orderId = expected(useParams().orderId);

  const orderFeatures = useAppSelector(selectOrderFeatures);
  const featureGridExpandedPropertyId = useAppSelector(selectFeatureGridExpandedPropertyId);

  const { setValue, watch } = useFormContext<PropertyGridForm>();
  const columns = watch('columns');

  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;

  const columnIndex = columns.findIndex(
    (column) => column.propertyId === propertyId && column.type === type,
  );

  const isPropertyColumn = type === 'property';

  const propertyDataState = useAppSelector((state) => selectPropertyDataById(state, propertyId));

  const { data: autofillData, isLoading: autofillIsLoading } =
    useOrchestrateAutofillPropertyAPI<PropertyDataWithOptionalReadonly>(
      {
        pathParams: { orderID: orderId, propertyId },
        // @ts-expect-error - Type mismatch because ToolkitContext['fetcherOptions'] is of type Record<string, string>
        queryParams: {
          includeBackupListingData: true,
          sources: [type],
        },
      },
      {
        enabled: !isPropertyColumn,
      },
    );

  const formFieldName = `columns.${columnIndex}.propertyData` as const;

  useEffect(() => {
    if (!isPropertyColumn && autofillData) setValue(formFieldName, autofillData);
  }, [autofillData, formFieldName, isPropertyColumn, setValue]);

  useEffect(() => {
    if (propertyDataState && isPropertyColumn) {
      if (propertyDataState.id === PROPERTY_GRID_SUBJECT_PROPERTY) {
        setValue(formFieldName, (propertyDataState as SubjectPropertyDataState).subjectData);
      } else {
        setValue(formFieldName, (propertyDataState as CompPropertyDataState).compData);
      }
    }
  }, [propertyDataState, formFieldName, isPropertyColumn, setValue]);

  const isLoading = isPropertyColumn ? !propertyDataState : autofillIsLoading;

  const propertyIsExpanded = featureGridExpandedPropertyId === propertyId;
  const columnIsVisible = isPropertyColumn || propertyIsExpanded;

  const columnIsSticky =
    isPropertyColumn &&
    (isDataSourceDrawerOpen ? propertyIsExpanded : propertyId === PROPERTY_GRID_SUBJECT_PROPERTY);

  const columnHasPaddingLeft = isPropertyColumn && propertyId !== PROPERTY_GRID_SUBJECT_PROPERTY;

  const gridSectionProps: PropertyGridSectionProps = {
    fieldTypes,
    columnIndex,
  };

  const sortedOrderFeatures: OrderFeature[] = orderFeatures
    .filter((feature) => feature !== 'manualCost')
    .sort((a, b) => a.localeCompare(b));
  if (orderFeatures.includes('manualCost')) sortedOrderFeatures.push('manualCost');

  let nbOfFieldColumns = fieldTypes.length;

  if (fieldTypes.includes('editableArvValue') && (gridType === 'AdjustmentsGrid' || !isSubject)) {
    nbOfFieldColumns -= 1;
  }

  if (!isPropertyColumn) nbOfFieldColumns = 1;

  return (
    <ErrorBoundary
      Fallback={ColumnErrorBoundaryFallback}
      onError={() => log.fatal(`Failed to display Property Grid Column ${propertyId} - ${type}`)}
    >
      <Block
        zIndex={columnIsSticky ? theme.zIndices.sticky : theme.zIndices.base}
        paddingLeft={columnHasPaddingLeft ? '12px' : '0'}
        __dangerDoNotUseSx={{
          opacity: isDataSourceDrawerOpen && !propertyIsExpanded ? 0.5 : 1,
          display: columnIsVisible ? 'block' : 'none',
        }}
        {...(columnIsSticky ? { position: 'sticky', left: 0 } : {})}
      >
        <Flex
          backgroundColor="surface1"
          border="1px solid strokeDefault"
          borderTopWidth="0"
          flexDirection="column"
          height="100%"
        >
          <HeaderPropertyGridSection columnIndex={columnIndex} gridType={gridType} />
          {isLoading ? (
            <Block height="100%" width="100%" position="relative">
              <LoadingScreen />
            </Block>
          ) : (
            <Grid
              backgroundColor="strokeDefault"
              gap="1px"
              gridTemplateColumns={`repeat(${nbOfFieldColumns}, 1fr)`}
            >
              {columnSections.map((Section) => (
                <Section
                  key={`${propertyId}-${type}-${Section.name}-section`}
                  {...gridSectionProps}
                />
              ))}
            </Grid>
          )}
        </Flex>
      </Block>
    </ErrorBoundary>
  );
};
